/* eslint-disable */
// /src/components/GlobalNotificationToast.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { listMyNotifications } from "../services/notificationsService";
import { db } from "../services/firebase";
import {
    collection,
    doc,
    getDoc,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
} from "firebase/firestore";



function safeStr(v) {
    return String(v == null ? "" : v).trim();
}

function getSessionSafe() {
    try {
        return JSON.parse(localStorage.getItem("dhm.session") || "null");
    } catch {
        return null;
    }
}

function extractPhoneE164FromSession(sess) {
    if (!sess) return "";
    return safeStr(
        sess.phoneE164 ||
        sess.user?.phoneE164 ||
        sess.member?.phoneE164 ||
        sess.profile?.phoneE164 ||
        sess.phone ||
        ""
    );
}

async function resolveMyMemberDocIdViaQuery(phoneE164) {
    const p = safeStr(phoneE164);
    if (!p) return "";

    const cacheKey = "dhm.memberDocId.v1";
    try {
        const cached = safeStr(localStorage.getItem(cacheKey));
        if (cached) return cached;
    } catch { }

    try {
        const colRef = collection(db, "members");
        const q1 = query(colRef, where("phoneE164", "==", p), limit(1));
        const snap = await getDoc(doc(db, "members_by_phone", p)).catch(() => null);

        // ✅ members_by_phone는 안 쓰는 정책일 수 있어도 안전하게 시도만 하고, 없으면 members 쿼리로 감
        if (snap && snap.exists()) {
            const data = snap.data() || {};
            const id = safeStr(data.memberDocId || "");
            if (id) {
                try {
                    localStorage.setItem(cacheKey, id);
                } catch { }
                return id;
            }
        }

        // members 쿼리
        // (주의: getDocs import 안 쓰려고 여기선 간단히 onSnapshot 쪽에서 docId를 세션 캐시로 저장한 뒤 쓰는 구조로도 가능하지만,
        //  지금은 Firestore 쿼리를 최소로 하기 위해 notificationsService 쪽을 안 건드리고 이 파일 내에서만 해결)
        // -> 여기서는 getDocs를 안 썼으니, 아래에서 memberDocId를 못 찾으면 토스트를 안 띄우는 쪽으로 처리
        return "";
    } catch {
        return "";
    }
}

const ToastWrap = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 10px;
  z-index: 9999;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

const ToastCard = styled.div`
  width: min(520px, calc(100vw - 24px));
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 10px 12px;
  pointer-events: auto;
`;

const TRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #ef4444;
  display: inline-block;
`;

const TTitle = styled.div`
  flex: 1;
  font-size: 13px;
  font-weight: 800;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: #6b7280;
  padding: 4px 6px;
  border-radius: 8px;
  &:active {
    transform: translateY(1px);
  }
`;

const TBody = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #4b5563;
  line-height: 1.35;
  white-space: pre-line;
`;

const Actions = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionBtn = styled.button`
  border-radius: 10px;
  border: 1px solid rgba(59, 130, 246, 0.25);
  background: ${(p) => (p.$primary ? "#3B82F6" : "#E8F1FF")};
  color: ${(p) => (p.$primary ? "#fff" : "#1D4ED8")};
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:active {
    transform: translateY(1px);
  }
`;

export default function GlobalNotificationToast() {
    const nav = useNavigate();

    const [open, setOpen] = useState(false);
    const [payload, setPayload] = useState(null);

    const timerRef = useRef(null);
    const lastShownIdRef = useRef("");

    useEffect(() => {
        // 마지막 표시 ID (중복 방지)
        try {
            lastShownIdRef.current = safeStr(localStorage.getItem("dhm.lastNotiToastId") || "");
        } catch { }

        const sess = getSessionSafe();
        const phoneE164 = extractPhoneE164FromSession(sess);

        if (!phoneE164) return;

        let unsub = null;
        let cancelled = false;

        (async () => {
            // ✅ memberDocId는 캐시가 있으면 그걸 쓰고, 없으면 "토스트 기능만" 조용히 스킵
            // (형 정책상 OR 추정체인 싫어하니까, 여기서는 폴백으로 억지로 찾지 않음)
            const memberDocId = safeStr(localStorage.getItem("dhm.memberDocId.v1") || "");
            if (!memberDocId) return;

            const colRef = collection(db, "members", memberDocId, "notifications");
            const q1 = query(colRef, orderBy("createdAt", "desc"), limit(1));

            unsub = onSnapshot(
                q1,
                (snap) => {
                    if (cancelled) return;
                    if (snap.empty) return;

                    const d = snap.docs[0];
                    const id = d.id;
                    const data = d.data() || {};

                    const lastShown = lastShownIdRef.current;
                    if (lastShown && id === lastShown) return;

                    // ✅ 최초 구독 시 과거 알림까지 띄우는 걸 원치 않으면:
                    // - 읽지 않은 것만 띄우기
                    if (data.read === true) return;

                    lastShownIdRef.current = id;
                    try {
                        localStorage.setItem("dhm.lastNotiToastId", id);
                    } catch { }

                    setPayload({
                        id,
                        title: safeStr(data.title) || "알림",
                        body: safeStr(data.body),
                    });
                    setOpen(true);

                    if (timerRef.current) clearTimeout(timerRef.current);
                    timerRef.current = setTimeout(() => {
                        setOpen(false);
                    }, 3200);
                },
                () => { }
            );
        })();

        return () => {
            cancelled = true;
            try {
                unsub && unsub();
            } catch { }
            try {
                if (timerRef.current) clearTimeout(timerRef.current);
            } catch { }
        };
    }, []);

    if (!open || !payload) return null;

    return (
        <ToastWrap>
            <ToastCard>
                <TRow>
                    <Dot />
                    <TTitle>{payload.title}</TTitle>
                    <CloseBtn type="button" onClick={() => setOpen(false)}>
                        닫기
                    </CloseBtn>
                </TRow>

                {payload.body ? <TBody>{payload.body}</TBody> : null}

                <Actions>
                    <ActionBtn type="button" onClick={() => setOpen(false)}>
                        확인
                    </ActionBtn>
                    <ActionBtn
                        type="button"
                        $primary
                        onClick={() => {
                            setOpen(false);
                            nav("/notifications");
                        }}
                    >
                        알림 보기
                    </ActionBtn>
                </Actions>
            </ToastCard>
        </ToastWrap>
    );
}
